import 'yet-another-react-lightbox/styles.css';

import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useState} from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

type LightboxImageProps = {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number;
  height?: number;
  onError?: () => void;
  withDownload?: boolean;
};

// A clickable thumbnail that opens a single-image lightbox on click.
export const LightboxImage = ({
  src,
  alt,
  className,
  style,
  width,
  height,
  onError,
  withDownload,
}: LightboxImageProps) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip
        content="Click to view large"
        trigger={
          <img
            src={src}
            alt={alt}
            className={className}
            style={{cursor: 'pointer', ...style}}
            width={width}
            height={height}
            onClick={() => setOpen(true)}
            onError={onError}
          />
        }
      />
      <SingleImageLightbox
        src={src}
        open={open}
        onClose={() => setOpen(false)}
        withDownload={withDownload}
      />
    </>
  );
};

type SingleImageLightboxProps = {
  src: string;
  open: boolean;
  onClose: () => void;
  withDownload?: boolean;
};

// The single source of truth for our one-image lightbox configuration.
export const SingleImageLightbox = ({
  src,
  open,
  onClose,
  withDownload,
}: SingleImageLightboxProps) => {
  return (
    <Lightbox
      plugins={withDownload ? [Fullscreen, Zoom, Download] : [Fullscreen, Zoom]}
      open={open}
      close={onClose}
      controller={{closeOnBackdropClick: true}}
      styles={{
        container: {backgroundColor: LIGHTBOX_BACKDROP},
        root: LIGHTBOX_CONTROL_VARS,
      }}
      slides={[{src}]}
      render={{
        // Only one image, so hide the carousel navigation buttons.
        buttonPrev: () => null,
        buttonNext: () => null,
      }}
      carousel={{finite: true}}
      zoom={{maxZoomPixelRatio: 5}}
    />
  );
};

const LIGHTBOX_BACKDROP = 'rgba(255, 255, 255, 0.9)';
const LIGHTBOX_CONTROL_VARS = {
  '--yarl__color_button': 'rgba(0, 0, 0, 0.7)',
  '--yarl__color_button_active': 'rgba(0, 0, 0, 1)',
};
